import { useEffect, useState } from 'react';
import { ref as dbRef, onValue, update } from 'firebase/database';
import { db } from 'utils/firebase-config';
import { getMessageTime } from 'helpers/utils';

export type ThreadReadState = {
  lastSeenCount: number;
  lastSeenTime: number;
};

export type ThreadReadsMap = Record<string, ThreadReadState>;

export type LastReplyRelative = { value: number; unit: string };

/** Session cache so room switches don't flash inflated unread before RTDB responds. */
const threadReadsCache = new Map<string, ThreadReadsMap>();

const cacheKey = (roomId: string | number, userId: string | number) =>
  `${roomId}:${userId}`;

const parseReadsSnapshot = (val: unknown): ThreadReadsMap => {
  if (!val || typeof val !== 'object') return {};
  const next: ThreadReadsMap = {};
  Object.keys(val as Record<string, any>).forEach((parentId) => {
    const entry = (val as Record<string, any>)[parentId];
    if (!entry) return;
    next[parentId] = {
      lastSeenCount: typeof entry.lastSeenCount === 'number' ? entry.lastSeenCount : 0,
      lastSeenTime: typeof entry.lastSeenTime === 'number' ? entry.lastSeenTime : 0,
    };
  });
  return next;
};

const normalizeThreads = (threads: any): any[] => {
  if (!threads) return [];
  if (Array.isArray(threads)) return threads.filter(Boolean);
  if (typeof threads === 'object') return Object.values(threads).filter(Boolean);
  return [];
};

/**
 * Unread replies for a parent message (Slack-style).
 * Counts replies from others after the user's lastSeenTime.
 * No prior read state → all others' replies count as unread (discoverability).
 */
export const getUnreadThreadCount = (
  threads: any,
  read: ThreadReadState | undefined,
  userPk: string | number | undefined
): number => {
  const list = normalizeThreads(threads);
  if (!list.length || userPk == null) return 0;

  const myPk = String(userPk);
  const lastSeenTime = read?.lastSeenTime ?? 0;

  return list.filter((t: any) => {
    const senderId = t?.sender?.id ?? t?.sender?.pk;
    if (senderId != null && String(senderId) === myPk) return false;
    return getMessageTime(t) > lastSeenTime;
  }).length;
};

/** Newest reply timestamp on a parent message (epoch ms). */
export const getLatestThreadTime = (threads: any): number => {
  const list = normalizeThreads(threads);
  return list.reduce((max, t) => Math.max(max, getMessageTime(t)), 0);
};

/** "Last reply X mins/hours/days ago" — returns value + unit for highlighted number. */
export const formatLastReplyRelative = (
  timestamp: number,
  now: number = Date.now()
): LastReplyRelative | null => {
  if (!timestamp) return null;

  const diffMs = Math.max(0, now - timestamp);
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return { value: diffMins, unit: 'min' };
  if (diffMins < 60) return { value: diffMins, unit: diffMins === 1 ? 'min' : 'mins' };

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return { value: diffHours, unit: diffHours === 1 ? 'hour' : 'hours' };

  const diffDays = Math.floor(diffHours / 24);
  return { value: diffDays, unit: diffDays === 1 ? 'day' : 'days' };
};

/**
 * Persist that the user has seen all current replies on a parent message.
 * Path: threadReads/{roomId}/{userId}/{parentMessageId}
 */
export const markThreadRead = async (
  roomId: string | number,
  userId: string | number,
  parentMessageId: string,
  threads: any
): Promise<void> => {
  if (roomId == null || userId == null || !parentMessageId) return;

  const list = normalizeThreads(threads);
  let newestTime = 0;
  list.forEach((t: any) => {
    const ts = getMessageTime(t);
    if (ts > newestTime) newestTime = ts;
  });

  const payload: ThreadReadState = {
    lastSeenCount: list.length,
    lastSeenTime: newestTime || Date.now(),
  };

  await update(
    dbRef(db, `threadReads/${roomId}/${userId}/${parentMessageId}`),
    payload
  );

  // Keep session cache in sync so main-chat badges don't flash stale unreads.
  const key = cacheKey(roomId, userId);
  const prev = threadReadsCache.get(key) ?? {};
  threadReadsCache.set(key, { ...prev, [parentMessageId]: payload });
};

/**
 * Live map of parentMessageId → lastSeen for the current user in a room.
 * `isReady` is false until the first RTDB snapshot for this room (or a cache hit),
 * so callers must not treat missing reads as "everything unread" while loading.
 */
export const useThreadReads = (
  roomId: string | number | null | undefined,
  userId: string | number | null | undefined
): { reads: ThreadReadsMap; isReady: boolean } => {
  const key =
    roomId != null && userId != null ? cacheKey(roomId, userId) : null;
  const cached = key ? threadReadsCache.get(key) : undefined;

  const [reads, setReads] = useState<ThreadReadsMap>(() => cached ?? {});
  const [isReady, setIsReady] = useState(() => Boolean(cached));

  useEffect(() => {
    if (roomId == null || userId == null) {
      setReads({});
      setIsReady(false);
      return undefined;
    }

    const roomKey = cacheKey(roomId, userId);
    const fromCache = threadReadsCache.get(roomKey);
    if (fromCache) {
      setReads(fromCache);
      setIsReady(true);
    } else {
      setReads({});
      setIsReady(false);
    }

    const readsRef = dbRef(db, `threadReads/${roomId}/${userId}`);
    const unsub = onValue(readsRef, (snapshot) => {
      const next = parseReadsSnapshot(snapshot.val());
      threadReadsCache.set(roomKey, next);
      setReads(next);
      setIsReady(true);
    });

    return () => unsub();
  }, [roomId, userId]);

  return { reads, isReady };
};
